/**
 * Read a multi-value filter parameter from a URL.
 *
 * Filter state is written as `name[]=a&name[]=b`, but Shopify rewrites those keys to a
 * plain `name=a&name=b` when the URL is loaded from the server (shared link, bookmark,
 * refresh). Older links also used a single comma-joined value. Accept all three so a
 * filtered URL always restores.
 *
 * @param {URLSearchParams} params - The parsed query string
 * @param {string} name - The filter name, without brackets
 * @returns {Array|null} The values, or null when the filter is absent
 */
function readListParam(params, name) {

  const bracketed = params.getAll(`${name}[]`);

  if (bracketed.length) {
    return bracketed;
  }

  const repeated = params.getAll(name);

  if (repeated.length > 1) {
    return repeated;
  }

  if (repeated.length === 1) {
    const values = repeated[0].split(',').filter(value => value !== '');
    return values.length ? values : null;
  }

  return null;
}

/**
 * StoneFilters custom element for handling stones filters (gemstones, diamonds, moissanites)
 *
 * @module stones-filters
 * @version 1.0.0
 * @extends HTMLElement
 */
class StoneFilters extends HTMLElement {

  get observedAttributes() {
    return ['stone-type'];
  }

  /**
   * Constructor for the StoneFilters element
   */
  constructor() {

    super();

    // Filter options
    this.arrayFilterOptions = ['shape_ids', 'color', 'colors', 'gem_stone_types', 'cut', 'clarity'];
    this.rangeFilterOptions = ['price_min', 'price_max', 'carat_min', 'carat_max'];

    // Initialize pagination state
    this.currentPage = this.getPageFromURL();
    this.perPage = 60;
    this.totalPages = 1;
    this.totalItems = 0;
    this.hasNext = false;
    this.hasPrev = false;
    this.allItems = []; // Store all loaded items

    // Initialize filter state from URL
    this.activeFilters = this.getFiltersFromURL();
    this.filterOptions = null;
    this.sortBy = this.getSortByFromURL();
    this.sortOrder = this.getSortOrderFromURL();
    this.sortOptions = [
      { id: 'price-asc', label: 'Price: Low to High', field: 'total_price', order: 'asc' },
      { id: 'price-desc', label: 'Price: High to Low', field: 'total_price', order: 'desc' },
      { id: 'carat-asc', label: 'Carat: Low to High', field: 'carat', order: 'asc' },
      { id: 'carat-desc', label: 'Carat: High to Low', field: 'carat', order: 'desc' }
    ];

    // Initialize view mode (default to 'grid', load from session storage)
    this.viewMode = this.getViewModeFromStorage();
    
    // Define table columns configuration (easily extensible)
    // Note: For gemstones, 'cut' column is labeled as 'Grade'
    this.tableColumns = [
      { key: 'shape', label: 'Shape', render: (item, stoneType) => this.renderShapeColumn(item, stoneType), sortable: false },
      { key: 'carat', label: 'Carat', render: (item) => this.renderCaratColumn(item), sortable: true, sortField: 'carat' },
      { key: 'price', label: 'Price', render: (item, stoneType) => this.renderPriceColumn(item, stoneType), sortable: true, sortField: 'total_price' },
      { key: 'cut', label: this.stoneType === 'gemstone' ? 'Grade' : 'Cut', render: (item, stoneType) => this.renderCutColumn(item, stoneType), sortable: true, sortField: 'cut' },
      { key: 'origin', label: 'Origin', render: (item) => this.renderOriginColumn(item), sortable: false }
    ];

    this.enableLoading();

    this.render();
  }

  /**
   * Connected callback
   */
  async connectedCallback() {

    // Load filter options first
    await this.loadFilterOptions();

    // Initialize event listeners
    this.initEvents();

    // Load all pages up to current page to maintain state
    await this.loadInitialPages();

    // Listen for browser navigation
    window.addEventListener('popstate', () => {
      this.handleBrowserNavigation();
    });
  }

  /**
   * Get the stone type
   * 
   * @returns {string} The stone type
   */
  get stoneType() {
    return this.getAttribute('stone-type') || 'diamond';
  }

  /**
   * Render base html structure
   */
  render() {
    this.innerHTML = `
      <div class="stone-filters laptop:flex laptop:gap-8">
        <div class="laptop:w-4/12 desktop:w-3/12">
          ${this.getFilterFormHtml()}
        </div>
        <div class="laptop:w-8/12 desktop:w-9/12">
          <div data-toolbar-container class="flex flex-wrap w-full pb-4 laptop:flex-nowrap laptop:justify-between laptop:gap-5">
            <div data-inline-active-facets class="w-full laptop:order-1 laptop:flex-1 laptop:flex laptop:items-center"></div>
            <div class="flex items-center justify-end w-full gap-4 laptop:order-2 laptop:w-auto laptop:flex-shrink-0 laptop:ml-auto">
              <div data-items-count></div>
              <div data-view-selector class="flex items-center">
                <button 
                  type="button" 
                  data-view-mode="table" 
                  class="view-mode-btn p-2 transition-opacity"
                  aria-label="Table view"
                  title="Table view"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="${this.viewMode === 'table' ? '' : 'opacity-50'}">
                    <path d="M7 19H21V17H7V19ZM7 13H21V11H7V13ZM7 5V7H21V5H7Z" fill="#A7A5A6"/>
                    <path d="M5 19H3V17H5V19ZM5 13H3V11H5V13ZM5 5V7H3V5H5Z" fill="#A7A5A6"/>
                  </svg>
                </button><button 
                  type="button" 
                  data-view-mode="grid" 
                  class="view-mode-btn p-2 transition-opacity"
                  aria-label="Grid view"
                  title="Grid view"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="${this.viewMode === 'grid' ? '' : 'opacity-50'}">
                    <path d="M7 19H5V17H7V19ZM7 13H5V11H7V13ZM7 5V7H5V5H7Z" fill="#231F20"/>
                    <path d="M13 19H11V17H13V19ZM13 13H11V11H13V13ZM13 5V7H11V5H13Z" fill="#231F20"/>
                    <path d="M19 19H17V17H19V19ZM19 13H17V11H19V13ZM19 5V7H17V5H19Z" fill="#231F20"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
          <div data-items-container class="${this.viewMode === 'grid' ? 'grid grid-cols-2 gap-4 tablet:grid-cols-3 tablet:gap-4 laptop:grid-cols-2 laptop:gap-5 desktop:grid-cols-3 desktop:gap-4' : ''}"></div>
          <div data-pagination-container></div>
        </div>
      </div>
    `;
  }

  /**
   * Get the html structure for the filter form
   * 
   * @returns {string} The html structure for the filter form
   */
  getFilterFormHtml() {
    return `
    <form data-filters-form class="flex flex-col border-t border-base-border pt-5 pb-5">

        <side-drawer id="stone-filters">
          <div class="flex pb-4 px-4">
              <div class="inline-flex items-center gap-2">
                  <span class="size-5">
                    <svg width="100%" height="100%" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10.6667 11.1663C10.2065 11.1663 9.83374 11.5401 9.83374 12.0003C9.83392 12.4604 10.2066 12.8333 10.6667 12.8333C11.1269 12.8333 11.4996 12.4604 11.4998 12.0003C11.4998 11.5401 11.127 11.1663 10.6667 11.1663ZM5.33374 7.16632C4.8735 7.16632 4.49976 7.54007 4.49976 8.00031C4.49993 8.46039 4.87361 8.83331 5.33374 8.83331C5.79372 8.83314 6.16657 8.46028 6.16675 8.00031C6.16675 7.54018 5.79383 7.1665 5.33374 7.16632ZM10.6667 3.16632C10.2065 3.16632 9.83374 3.54007 9.83374 4.0003C9.83392 4.46039 10.2066 4.83331 10.6667 4.83331C11.1269 4.83331 11.4996 4.46039 11.4998 4.0003C11.4998 3.54007 11.127 3.16632 10.6667 3.16632ZM10.6667 10.1663C11.5059 10.1663 12.2118 10.7309 12.4294 11.5003L13.3337 11.5003C13.6097 11.5005 13.8337 11.7243 13.8337 12.0003C13.8336 12.2762 13.6096 12.5001 13.3337 12.5003L12.4285 12.5003C12.2105 13.2693 11.5056 13.8333 10.6667 13.8333C9.82783 13.8333 9.12194 13.2694 8.90405 12.5003L2.66675 12.5003C2.39071 12.5003 2.16692 12.2763 2.16675 12.0003C2.16675 11.7242 2.39061 11.5003 2.66675 11.5003L8.90308 11.5003C9.12068 10.7307 9.82752 10.1663 10.6667 10.1663ZM10.6667 2.16632C11.5059 2.16632 12.2118 2.73087 12.4294 3.5003L13.3337 3.5003C13.6097 3.50048 13.8337 3.72427 13.8337 4.0003C13.8336 4.27619 13.6096 4.50013 13.3337 4.5003L12.4285 4.5003C12.2105 5.2693 11.5056 5.83331 10.6667 5.83331C9.82783 5.83331 9.12194 5.26943 8.90405 4.5003L2.66675 4.50031C2.39071 4.50031 2.16692 4.2763 2.16675 4.00031C2.16675 3.72416 2.39061 3.50031 2.66675 3.50031L8.90308 3.5003C9.12068 2.73074 9.82752 2.16632 10.6667 2.16632ZM5.33374 6.16632C6.17277 6.16647 6.87881 6.73092 7.09644 7.5003L13.3337 7.5003C13.6097 7.50048 13.8337 7.72427 13.8337 8.0003C13.8336 8.27619 13.6096 8.50013 13.3337 8.5003L7.09546 8.5003C6.87755 9.26925 6.17246 9.83317 5.33374 9.83331C4.49476 9.83331 3.7879 9.26952 3.57007 8.50031L2.66675 8.50031C2.39071 8.50031 2.16692 8.2763 2.16675 8.00031C2.16675 7.72416 2.39061 7.50031 2.66675 7.50031L3.57007 7.50031C3.7877 6.73078 4.49454 6.16632 5.33374 6.16632Z" fill="currentColor"/>
                    </svg>
                  </span>
                  <span class="uppercase">Filters</span>
              </div>
          </div>
          <div class="flex flex-col gap-4 h-[calc(100%-70px)]">
            <div data-drawer-active-facets class="px-4 hidden"></div>
            <div data-drawer-facets class="overflow-y-auto h-full px-4"></div>
            <div class="flex justify-end shrink-0 px-4">
                <button data-filter-submit type="button" class="btn btn-secondary w-full">
                    Show Results
                </button>
            </div>
          </div>
        </side-drawer>

        <div data-sidebar-facets class="w-full flex flex-col gap-5"></div>

        <button type="button" class="btn btn-primary rounded-none fixed w-[200px] bottom-6 left-0 right-0 m-auto z-10 laptop:hidden" data-drawer-target="stone-filters" aria-label="Filters">
            <span class="size-5">
              <svg width="100%" height="100%" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10.6667 11.1663C10.2065 11.1663 9.83374 11.5401 9.83374 12.0003C9.83392 12.4604 10.2066 12.8333 10.6667 12.8333C11.1269 12.8333 11.4996 12.4604 11.4998 12.0003C11.4998 11.5401 11.127 11.1663 10.6667 11.1663ZM5.33374 7.16632C4.8735 7.16632 4.49976 7.54007 4.49976 8.00031C4.49993 8.46039 4.87361 8.83331 5.33374 8.83331C5.79372 8.83314 6.16657 8.46028 6.16675 8.00031C6.16675 7.54018 5.79383 7.1665 5.33374 7.16632ZM10.6667 3.16632C10.2065 3.16632 9.83374 3.54007 9.83374 4.0003C9.83392 4.46039 10.2066 4.83331 10.6667 4.83331C11.1269 4.83331 11.4996 4.46039 11.4998 4.0003C11.4998 3.54007 11.127 3.16632 10.6667 3.16632ZM10.6667 10.1663C11.5059 10.1663 12.2118 10.7309 12.4294 11.5003L13.3337 11.5003C13.6097 11.5005 13.8337 11.7243 13.8337 12.0003C13.8336 12.2762 13.6096 12.5001 13.3337 12.5003L12.4285 12.5003C12.2105 13.2693 11.5056 13.8333 10.6667 13.8333C9.82783 13.8333 9.12194 13.2694 8.90405 12.5003L2.66675 12.5003C2.39071 12.5003 2.16692 12.2763 2.16675 12.0003C2.16675 11.7242 2.39061 11.5003 2.66675 11.5003L8.90308 11.5003C9.12068 10.7307 9.82752 10.1663 10.6667 10.1663ZM10.6667 2.16632C11.5059 2.16632 12.2118 2.73087 12.4294 3.5003L13.3337 3.5003C13.6097 3.50048 13.8337 3.72427 13.8337 4.0003C13.8336 4.27619 13.6096 4.50013 13.3337 4.5003L12.4285 4.5003C12.2105 5.2693 11.5056 5.83331 10.6667 5.83331C9.82783 5.83331 9.12194 5.26943 8.90405 4.5003L2.66675 4.50031C2.39071 4.50031 2.16692 4.2763 2.16675 4.00031C2.16675 3.72416 2.39061 3.50031 2.66675 3.50031L8.90308 3.5003C9.12068 2.73074 9.82752 2.16632 10.6667 2.16632ZM5.33374 6.16632C6.17277 6.16647 6.87881 6.73092 7.09644 7.5003L13.3337 7.5003C13.6097 7.50048 13.8337 7.72427 13.8337 8.0003C13.8336 8.27619 13.6096 8.50013 13.3337 8.5003L7.09546 8.5003C6.87755 9.26925 6.17246 9.83317 5.33374 9.83331C4.49476 9.83331 3.7879 9.26952 3.57007 8.50031L2.66675 8.50031C2.39071 8.50031 2.16692 8.2763 2.16675 8.00031C2.16675 7.72416 2.39061 7.50031 2.66675 7.50031L3.57007 7.50031C3.7877 6.73078 4.49454 6.16632 5.33374 6.16632Z" fill="currentColor"/>
              </svg>
            </span>
            <span class="flex items-center gap-1">Stone Filters</span>
        </button>

      </form>`;
  }

  /**
   * Get page number from URL parameters
   * 
   * @returns {number} The current page from URL or 1 if not found
   */
  getPageFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    const page = parseInt(urlParams.get('page')) || 1;
    return Math.max(1, page);
  }

  /**
   * Get filters from URL parameters
   * 
   * @returns {Object} The active filters object from URL
   */
  getFiltersFromURL() {

    const urlParams = new URLSearchParams(window.location.search);
    const filters = {};

    // Array filters. Filter state is pushed into the URL as `name[]=a&name[]=b`,
    // but Shopify rewrites those keys to a plain `name=a&name=b` on a full page
    // load, so a shared or refreshed filter URL only round-trips if both
    // spellings are accepted.
    this.arrayFilterOptions.forEach(param => {
      const values = readListParam(urlParams, param);
      if (values) filters[param] = values;
    });

    // Range filters
    this.rangeFilterOptions.forEach(param => {
      const value = urlParams.get(param);
      if (value) filters[param] = value;
    });

    // Handle lab_grown as a single-value filter (radio button)
    // Support both with and without brackets for backward compatibility
    const labGrownWithBrackets = urlParams.getAll('lab_grown[]');
    const labGrownWithoutBrackets = urlParams.get('lab_grown');
    if (labGrownWithBrackets.length > 0) {
      filters.lab_grown = labGrownWithBrackets[0]; // Take first value for radio
    } else if (labGrownWithoutBrackets) {
      filters.lab_grown = labGrownWithoutBrackets;
    }

    return filters;
  }

  /**
   * Get sort field from URL parameters
   * 
   * @returns {string} The sort field from URL or default 'total_price'
   */
  getSortByFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    // TODO: Change to 'total_price' to 'price' or new stones or best sellers
    return urlParams.get('sort_by') || 'total_price';
  }

  /**
   * Get sort order from URL parameters
   * 
   * @returns {string} The sort order from URL or default 'asc'
   */
  getSortOrderFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('sort_order') || 'asc';
  }

  /**
   * Get view mode from session storage
   * 
   * @returns {string} The view mode from storage or default 'grid'
   */
  getViewModeFromStorage() {
    try {
      const stored = sessionStorage.getItem('stones-view-mode');
      return stored === 'table' ? 'table' : 'grid';
    } catch (e) {
      return 'grid';
    }
  }

  /**
   * Save view mode to session storage
   * 
   * @param {string} mode - The view mode to save ('grid' or 'list')
   */
  saveViewModeToStorage(mode) {
    try {
      sessionStorage.setItem('stones-view-mode', mode);
    } catch (e) {
      console.error('Failed to save view mode to session storage:', e);
    }
  }

  /**
   * Enable the loading state
   */
  enableLoading() {
    this.classList.add('loading');
    
    // Use global theme loading component - multiple access methods for reliability
    if (window.theme && window.theme.ThemeLoader) {
      window.theme.ThemeLoader.enable().catch(console.error);
    } else if (window.theme && window.theme.loading) {
      window.theme.loading.enable();
    }
  }

  /**
   * Disable the loading state
   */
  disableLoading() {
    this.classList.remove('loading');
    
    // Use global theme loading component - multiple access methods for reliability
    if (window.theme && window.theme.ThemeLoader) {
      window.theme.ThemeLoader.disable().catch(console.error);
    } else if (window.theme && window.theme.loading) {
      window.theme.loading.disable();
    }
  }

  /**
   * Initialize event listeners
   */
  initEvents() {

    // Handle filter form changes
    const filtersForm = this.querySelector('[data-filters-form]');
    if (filtersForm) {
      filtersForm.addEventListener('change', this.onFilterChange.bind(this));
    }

    // Handle filter submit button
    const filterSubmit = this.querySelector('[data-filter-submit]');
    if (filterSubmit) {
      filterSubmit.addEventListener('click', (event) => {
        event.preventDefault();
        this.querySelector('side-drawer').close();
      });
    }

    // Handle view mode selector buttons
    const viewSelector = this.querySelector('[data-view-selector]');
    if (viewSelector) {
      const viewButtons = viewSelector.querySelectorAll('[data-view-mode]');
      viewButtons.forEach(button => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          const newMode = button.getAttribute('data-view-mode');
          this.setViewMode(newMode);
        });
      });
    }
  }

  /**
   * Set view mode and update UI
   * 
   * @param {string} mode - The view mode to set ('grid' or 'table')
   */
  setViewMode(mode) {
    if (mode !== 'grid' && mode !== 'table') return;
    
    this.viewMode = mode;
    this.saveViewModeToStorage(mode);
    
    // Update view selector buttons
    const viewButtons = this.querySelectorAll('[data-view-mode]');
    viewButtons.forEach(button => {
      const buttonMode = button.getAttribute('data-view-mode');
      const svg = button.querySelector('svg');
      
      if (svg) {
        if (buttonMode === mode) {
          // Active view: full opacity
          svg.classList.remove('opacity-50');
        } else {
          // Inactive view: 50% opacity
          svg.classList.add('opacity-50');
        }
      }
    });
    
    // Update container classes
    const container = this.querySelector('[data-items-container]');
    if (container) {
      if (mode === 'grid') {
        container.className = 'grid grid-cols-2 gap-4 tablet:grid-cols-3 tablet:gap-4 laptop:grid-cols-2 laptop:gap-5 desktop:grid-cols-3 desktop:gap-4';
      } else {
        container.className = '';
      }
      
      // Re-render items with new view mode
      if (this.allItems.length > 0) {
        this.renderItems(this.allItems);
      }
    }
  }

  /**
   * Render shape column (image + shape name)
   */
  renderShapeColumn(item, stoneType) {
    const imageUrl = window.theme.stones.getStoneImage(item, stoneType);
    const shapeName = window.theme.stones.getShapeName(item.shape_id);
    const slug = window.theme.stones.getStoneSlug(item, stoneType);
    let href = `/products/gemstone?stone=${slug}`;

    if (stoneType === 'color-diamond' || stoneType === 'diamond') {
      href = `/products/diamond?stone=${slug}`;
    } else if (stoneType === 'moissanite') {
      href = `/products/moissanite?stone=${slug}`;
    }

    return `
      <a href="${href}" class="flex items-center gap-3 group hover:no-underline">
        <img 
          src="${imageUrl}" 
          alt="${shapeName}"
          width="30" height="30"
          class="w-[30px] h-[30px] object-cover block shrink-0"
          loading="lazy"
        />
        <span class="text-sm group-hover:underline">${shapeName}</span>
      </a>
    `;
  }

  /**
   * Render carat column
   */
  renderCaratColumn(item) {
    return `<span class="text-sm">${item.carat}</span>`;
  }

  /**
   * Render price column
   */
  renderPriceColumn(item, stoneType) {
    const price = window.theme.stones.getPrice(item, stoneType);
    return `<span class="text-sm font-medium text-primary-500">${theme.money.format(price, true)}</span>`;
  }

  /**
   * Render cut column
   */
  renderCutColumn(item, stoneType) {
    if (stoneType === 'gemstone') {
      return `<span class="text-sm">${item.gem_grade || 'N/A'}</span>`;
    }
    return `<span class="text-sm">${item.cut || 'N/A'}</span>`;
  }

  /**
   * Render origin column
   */
  renderOriginColumn(item) {
    const origin = item.lab_grown ? 'Lab Created' : 'Natural';
    return `<span class="text-sm">${origin}</span>`;
  }

  /**
   * Get the stones from the API
   * 
   * @param {number} page - The page number to fetch (default: 1)
   * @returns {Promise<Object>} The API response with stones data and pagination
   */
  async getStones(page = 1) {

    return await window.theme.stones.getFilteredStones(this.stoneType, {
      page: page,
      per_page: this.perPage,
      filters: this.expandCutFilter(this.activeFilters),
      sort_by: this.sortBy,
      sort_order: this.sortOrder
    });
  }

  /**
   * The Cut filter follows the GIA scale (Excellent → Poor), but the catalog also holds a
   * small number of legacy "Ideal" stones that GIA does not grade. Request them alongside
   * "Excellent" so they remain reachable, without leaking "Ideal" into the URL state.
   *
   * @param {Object} filters - The active filters
   * @returns {Object} The filters to send to the API
   */
  expandCutFilter(filters) {

    if (!Array.isArray(filters.cut) || !filters.cut.includes('Excellent') || filters.cut.includes('Ideal')) {
      return filters;
    }

    return { ...filters, cut: [...filters.cut, 'Ideal'] };
  }

  /**
   * Load filter options from API
   */
  async loadFilterOptions() {
    try {
      this.filterOptions = await window.theme.stones.getFilterOptions(this.stoneType);
      this.renderFilters();
    } catch (error) {
      console.error('Error loading filter options:', error);
    }
  }

  /**
   * Render the filter UI
   */
  renderFilters() {

    // Reset filters
    const sidebarFacets = this.querySelector('[data-sidebar-facets]');
    const drawerFacets = this.querySelector('[data-drawer-facets]');
    if (!drawerFacets || !sidebarFacets) return;
    drawerFacets.innerHTML = '';
    sidebarFacets.innerHTML = '';

    // Render
    this.renderSortBy();
    this.renderActiveFilters();

    if (this.stoneType == 'gemstone') {

      this.createStoneFilter();
      this.createOriginFilter();
      this.createGemStoneTypesFilter();
      this.createShapeFilter();
      this.createCaratRangeFilter();
      this.createPriceRangeFilter();
    }

    if (this.stoneType == 'moissanite') {

      this.createStoneFilter();
      this.createShapeFilter();
      this.createCaratRangeFilter();
      this.createPriceRangeFilter();
    }

    if (this.stoneType == 'diamond') {

      this.createStoneFilter();
      this.createOriginFilter();
      this.createShapeFilter();
      this.createColorFilter();
      this.createCaratRangeFilter();
      this.createClarityFilter();
      this.createCutFilter();
      this.createPriceRangeFilter();
    }

    if (this.stoneType == 'color-diamond') {

      this.createStoneFilter();
      this.createOriginFilter();
      this.createShapeFilter();
      this.createColorDiamondFilter();
      this.createCaratRangeFilter();
      this.createPriceRangeFilter();
    }
  }

  /**
   * Render inline active filters for desktop
   */
  renderInlineActiveFilters() {
    const inlineActiveFacetsElement = this.querySelector('[data-inline-active-facets]');
    
    if (inlineActiveFacetsElement) {
      // Only show facets if there are actual filters (not just sort)
      if (this.hasActiveFilters()) {
        const activeFacetsHtml = this.getActiveFiltersHtml();
        inlineActiveFacetsElement.classList.remove('hidden');
        inlineActiveFacetsElement.innerHTML = activeFacetsHtml;
      } else {
        inlineActiveFacetsElement.classList.add('hidden');
        inlineActiveFacetsElement.innerHTML = '';
      }
    }
  }

  /**
   * Render mobile active filters
   */
  renderMobileActiveFilters() {
    const mobileActiveFacetsElement = this.querySelector('[data-mobile-active-facets]');
    
    if (mobileActiveFacetsElement) {
      const activeFacetsHtml = this.getActiveFiltersHtml();
      
      if (this.hasActiveFilters()) {
        mobileActiveFacetsElement.classList.remove('hidden');
        mobileActiveFacetsElement.innerHTML = activeFacetsHtml;
      } else {
        mobileActiveFacetsElement.classList.add('hidden');
        mobileActiveFacetsElement.innerHTML = '';
      }
    }
  }

  /**
   * Check if there are any active filters (excluding sort)
   * @returns {boolean} True if there are active filters
   */
  hasActiveFilters() {
    return Object.keys(this.activeFilters).length > 0;
  }

  /**
   * Render active filters in both inline and mobile containers
   */
  renderActiveFilters() {
    this.renderInlineActiveFilters();
    this.renderMobileActiveFilters();
  }

  /**
   * Get the HTML for active filters display
   * 
   * @returns {string} HTML string for active filters
   */
  getActiveFiltersHtml() {

    let html = '';
    
    Object.keys(this.activeFilters).forEach(filterKey => {

      const filterValue = this.activeFilters[filterKey];
      
      if (Array.isArray(filterValue)) {

        filterValue.forEach(value => {

          const label = this.getFilterDisplayLabel(filterKey, value);
          
          html += `<stone-filter-remove key="${filterKey}" value="${value}" label="${label}"></stone-filter-remove>`;
        });

      } else {

        const label = this.getFilterDisplayLabel(filterKey, filterValue);

        html += `<stone-filter-remove key="${filterKey}" value="${filterValue}" label="${label}"></stone-filter-remove>`;
      }
    });
    
    if (Object.keys(this.activeFilters).length > 1) {

      html += `<stone-filter-remove key="reset-all" label="Reset All"></stone-filter-remove>`;
    }

    return `<div data-active-facets class="w-full flex flex-wrap gap-1 items-center border-b pb-3 mb-4 laptop:border-b-0 laptop:mb-0 laptop:pb-0">${html}</div>`;
  }

  /**
   * Get display label for a filter
   * @param {string} filterKey - The filter key
   * @param {string} filterValue - The filter value
   * @returns {string} The display label
   */
  getFilterDisplayLabel(filterKey, filterValue) {

    switch (filterKey.replace('[]', '')) {

      case 'shape_ids':
        return `Shape: ${window.theme.stones.getShapeName(filterValue) || filterValue}`;
        
      case 'color':
        return `Color: ${filterValue}`;
        
      case 'colors':
        return `Color: ${filterValue}`;
        
      case 'gem_stone_types':
        return `Gem Type: ${filterValue}`;

      case 'lab_grown':
        return `Origin: ${filterValue}`;
        
      case 'price_min':
        return `Min Price: $${parseInt(filterValue).toLocaleString()}`;
        
      case 'price_max':
        return `Max Price: $${parseInt(filterValue).toLocaleString()}`;
        
      case 'carat_min':
        return `Min Carat: ${filterValue}`;
        
      case 'carat_max':
        return `Max Carat: ${filterValue}`;
        
      case 'cut':
        return `Cut: ${filterValue}`;
        
      case 'clarity':
        return `Clarity: ${filterValue}`;
        
      default:
        return `${filterKey}: ${filterValue}`;
    }
  }

  /**
   * Render sort by dropdown
   */
  renderSortBy() {
    const sortByContainer = this.querySelector('[data-sort-by]');
    if (!sortByContainer) return;

    const currentSort = this.sortOptions.find(option => 
      option.field === this.sortBy && option.order === this.sortOrder
    );

    let optionsHtml = '';
    this.sortOptions.forEach(option => {
      const selected = currentSort && option.id === currentSort.id ? 'selected' : '';
      optionsHtml += `<option value="${option.id}" ${selected}>${option.label}</option>`;
    });

    sortByContainer.innerHTML = `
      <div class="flex items-center gap-1 text-primary-300">
        <span class="block w-[90px]">Sort by:</span>
        <select name="sort_by" class="border-none py-0 pl-0 font-normal focus-within:text-primary-500 focus-within:underline">
          ${optionsHtml}
        </select>
      </div>
    `;
  }

  /**
   * Handle filter change events
   * 
   * @param {Event} event - The change event
   */
  onFilterChange(event) {

    const { name, value, type, checked } = event.target;

    // Handle sort by change
    if (name === 'sort_by') {
      this.applySortBy(value);
      return;
    }

    // Handle filter changes
    if (type === 'checkbox' || type === 'radio') {
      if (checked) {
        this.addFilter(name, value);
      } else {
        this.removeFilter(name, value);
      }
    } else if (type === 'text') {
      this.handleTextFilter(name, value);
    }
  }

  /**
   * Handle range filter inputs
   * 
   * @param {string} name - Input name (price_min, price_max, etc.)
   * @param {string} value - Input value
   */
  handleRangeFilter(name, value) {
    // Debounce range filter changes
    clearTimeout(this.rangeFilterTimeout);
    
    this.rangeFilterTimeout = setTimeout(() => {
      if (value && value.trim() !== '') {
        this.activeFilters[name] = [value];
      } else {
        delete this.activeFilters[name];
      }
      this.resetAndFetchGems();
      // Re-render filters to sync UI state
      this.renderFilters();
    }, 500); // Wait 500ms after user stops typing
  }

  /**
   * Handle text filter inputs
   * 
   * @param {string} name - Input name
   * @param {string} value - Input value
   */
  handleTextFilter(name, value) {
    // Handle range filters that need to be split into min/max pairs
    if (name === 'carat_range') {
      // Split comma-separated values into carat_min and carat_max
      if (value && value.trim() !== '') {
        const values = value.split(',').filter(v => v && v.trim() !== '');
        if (values.length >= 2) {
          this.activeFilters['carat_min'] = values[0];
          this.activeFilters['carat_max'] = values[values.length - 1];
        } else if (values.length === 1) {
          // If only one value, set both min and max to the same value
          this.activeFilters['carat_min'] = values[0];
          this.activeFilters['carat_max'] = values[0];
        }
      } else {
        // Remove filters if value is empty
        delete this.activeFilters['carat_min'];
        delete this.activeFilters['carat_max'];
      }
      // Remove the old carat_range entry if it exists
      delete this.activeFilters[name];
    } else if (name === 'price') {
      // Split comma-separated values into price_min and price_max
      if (value && value.trim() !== '') {
        const values = value.split(',').filter(v => v && v.trim() !== '');
        if (values.length >= 2) {
          this.activeFilters['price_min'] = values[0];
          this.activeFilters['price_max'] = values[values.length - 1];
        } else if (values.length === 1) {
          // If only one value, set both min and max to the same value
          this.activeFilters['price_min'] = values[0];
          this.activeFilters['price_max'] = values[0];
        }
      } else {
        // Remove filters if value is empty
        delete this.activeFilters['price_min'];
        delete this.activeFilters['price_max'];
      }
      // Remove the old price entry if it exists
      delete this.activeFilters[name];
    } else if (name === 'cut' || name === 'clarity') {
      // Handle cut and clarity as array filters (multiple values from slider range)
      // The value is a comma-separated string of all selected values
      if (value && value.trim() !== '') {
        const values = value.split(',').filter(v => v && v.trim() !== '');
        if (values.length > 0) {
          this.activeFilters[name] = values;
        }
      } else {
        // Remove filter if value is empty
        delete this.activeFilters[name];
      }
    } else {
      // Handle other text filters normally
      if (value === '' || value === null || value === undefined) {
        delete this.activeFilters[name];
      } else {
        this.activeFilters[name] = value;
      }
    }
    
    this.resetAndFetchGems();
    this.renderFilters();
  }

  /**
   * Reset all active filters
   */
  resetAllFilters() {
    this.activeFilters = {};
    this.sortBy = 'total_price';
    this.sortOrder = 'asc';
    this.resetAndFetchGems();

    // Re-render filters to sync UI state
    this.renderFilters();
  }

  /**
   * Remove a specific filter
   * @param {string} filterKey - The filter key to remove
   * @param {string} filterValue - The specific value to remove
   */
  removeActiveFilter(filterKey, filterValue) {
    if (filterKey === 'sort') {
      // Reset sort to default
      this.sortBy = 'total_price';
      this.sortOrder = 'asc';
    } else if (filterKey === 'carat_min' || filterKey === 'carat_max') {
      // Remove both carat_min and carat_max when removing either one
      delete this.activeFilters['carat_min'];
      delete this.activeFilters['carat_max'];
    } else if (filterKey === 'price_min' || filterKey === 'price_max') {
      // Remove both price_min and price_max when removing either one
      delete this.activeFilters['price_min'];
      delete this.activeFilters['price_max'];
    } else if (this.activeFilters[filterKey]) {
      if (Array.isArray(this.activeFilters[filterKey])) {
        // Remove specific value from array
        this.activeFilters[filterKey] = this.activeFilters[filterKey].filter(v => v !== filterValue);
        if (this.activeFilters[filterKey].length === 0) {
          delete this.activeFilters[filterKey];
        }
      } else {
        // Remove the entire filter
        delete this.activeFilters[filterKey];
      }
    }
    
    this.resetAndFetchGems();
    // Re-render filters to sync UI state
    this.renderFilters();
  }

  /**
   * Add a filter
   * 
   * @param {string} name - Filter name
   * @param {string} value - Filter value
   */
  addFilter(name, value) {

    // Handle array-type filters
    if (this.arrayFilterOptions.includes(name)) {

      if (!this.activeFilters[name]) {
        this.activeFilters[name] = [];
      }
      
      if (!this.activeFilters[name].includes(value)) {
        this.activeFilters[name].push(value);
      }

    } else {

      // Handle single-value filters
      // If value is empty, remove the filter instead of setting it to empty string
      if (value === '' || value === null || value === undefined) {
        delete this.activeFilters[name];
      } else {
        this.activeFilters[name] = value;
      }
    }

    this.resetAndFetchGems();

    // Re-render filters to sync UI state
    this.renderFilters();
  }

  /**
   * Remove a filter
   * 
   * @param {string} name - Filter name
   * @param {string} value - Filter value
   */
  removeFilter(name, value) {
    if (this.activeFilters[name]) {
      // Handle array-type filters
      if (Array.isArray(this.activeFilters[name])) {
        this.activeFilters[name] = this.activeFilters[name].filter(v => v !== value);
        
        if (this.activeFilters[name].length === 0) {
          delete this.activeFilters[name];
        }
      } else {
        // Handle single-value filters
        delete this.activeFilters[name];
      }
    }

    this.resetAndFetchGems();
    // Re-render filters to sync UI state
    this.renderFilters();
  }

  /**
   * Apply sort by option
   * 
   * @param {string} sortId - Sort option ID
   */
  applySortBy(sortId) {
    const sortOption = this.sortOptions.find(option => option.id === sortId);
    if (sortOption) {
      this.sortBy = sortOption.field;
      this.sortOrder = sortOption.order;
      this.resetAndFetchGems();
      // Re-render filters to sync UI state
      this.renderFilters();
    }
  }

  /**
   * Reset gems and fetch with current filters
   */
  resetAndFetchGems() {
    this.allItems = [];
    this.currentPage = 1;
    this.updateURL(1);
    this.loadInitialPages();
    this.renderActiveFilters();
  }

  /**
   * Update URL with current state (page, filters, sort) without page reload
   * 
   * @param {number} page - The page number to set in URL
   */
  updateURL(page = this.currentPage) {
    const url = new URL(window.location);
    
    // Clear all existing search params to start fresh
    url.search = '';
    
    // Add page parameter
    if (page > 1) {
      url.searchParams.set('page', page);
    }
    
    // Add sort parameters
    if (this.sortBy !== 'total_price') {
      url.searchParams.set('sort_by', this.sortBy);
    }
    if (this.sortOrder !== 'asc') {
      url.searchParams.set('sort_order', this.sortOrder);
    }
    
    // Add filter parameters
    if (this.activeFilters) {
      Object.keys(this.activeFilters).forEach(key => {
        const value = this.activeFilters[key];
        if (value !== null && value !== undefined && value !== '') {
          if (Array.isArray(value)) {
            if (this.arrayFilterOptions.includes(key)) {
              // Handle multi-select filters
              value.forEach(v => url.searchParams.append(`${key}[]`, v));
            } else {
              // Handle range filters (price_min, price_max, etc.)
              if (value.length > 0 && value[0] !== '') {
                url.searchParams.set(key, value[0]);
              }
            }
          } else {
            url.searchParams.set(key, value);
          }
        }
      });
    }
    
    // Update URL without page reload
    window.history.pushState({ 
      page, 
      filters: this.activeFilters,
      sortBy: this.sortBy,
      sortOrder: this.sortOrder
    }, '', url);
  }

  /**
   * Load all pages up to the current page (for initial load)
   */
  async loadInitialPages() {
    await this.loadUpToPage(this.currentPage);
  }

  /**
   * Load all pages up to specified page
   * 
   * @param {number} targetPage - The target page to load up to
   */
  async loadUpToPage(targetPage) {
    this.enableLoading();

    try {

      // Load pages sequentially to maintain order
      for (let page = 1; page <= targetPage; page++) {

        if (page === 1 || page > this.allItems.length / this.perPage) {

          const result = await this.getStones(page);
          
          if (page === 1) {
            this.allItems = [...result.data];
          } else {
            this.allItems = [...this.allItems, ...result.data];
          }
          
          // Update pagination state with latest response
          this.updatePaginationState(result.pagination);
        }
      }

      this.currentPage = targetPage;
      this.renderCurrentItems();
      this.renderLoadMoreButton();
      
    } catch (error) {
      console.error('Error loading pages:', error);
    } finally {
      this.disableLoading();
    }
  }

  /**
   * Update pagination state from API response
   * 
   * @param {Object} pagination - The pagination object from API
   */
  updatePaginationState(pagination) {
    this.currentPage = pagination.current_page;
    this.perPage = pagination.per_page;
    this.totalPages = pagination.total_pages;
    this.totalItems = pagination.total || pagination.total_items;
    this.hasNext = pagination.has_next || pagination.has_next_page;
    this.hasPrev = pagination.has_prev || pagination.has_prev_page;
  }

  /**
   * Render current items based on loaded data
   */
  renderCurrentItems() {
    const itemsToShow = this.allItems.slice(0, this.currentPage * this.perPage);
    this.renderItemsCount(this.totalItems);
    this.renderItems(itemsToShow);
  }

  /**
   * Get the display name for the stone type
   * 
   * @param {boolean} plural - Whether to return plural form
   * @returns {string} The display name
   */
  getStoneTypeDisplayName(plural = false) {
    const displayNames = {
      'diamond': plural ? 'diamonds' : 'diamond',
      'color-diamond': plural ? 'color diamonds' : 'color diamond',
      'gemstone': plural ? 'gemstones' : 'gemstone',
      'moissanite': plural ? 'moissanites' : 'moissanite'
    };
    return displayNames[this.stoneType] || (plural ? this.stoneType + 's' : this.stoneType);
  }

  /**
   * Render the items count
   * 
   * @param {number} count - The number of items
   */
  renderItemsCount(count) {
    this.querySelectorAll('[data-items-count]').forEach(container => {
      container.innerHTML = `
        <div class="whitespace-nowrap">${count.toLocaleString()} ${this.getStoneTypeDisplayName(count > 1)}</div>
      `;
    });
  }

  /**
   * Render items based on loaded data
   * 
   * @param {Array} items - The items to render
   */
  renderItems(items) {

    const  container = this.querySelector('[data-items-container]');

    if (!container) return;

    container.innerHTML = '';

    if (this.viewMode === 'table') {
      // Render table view
      this.renderTableView(items, container);
    } else {
      // Render grid view
      items.forEach(item => {
        const stoneCard = document.createElement('legacy-stone-card');
        stoneCard.setAttribute('stone-type', this.stoneType);
        stoneCard.setAttribute('view-mode', this.viewMode);
        stoneCard.stoneData = item;
        container.appendChild(stoneCard);
      });
    }
  }

  /**
   * Handle column header click for sorting
   * 
   * @param {string} sortField - The field to sort by
   */
  handleColumnSort(sortField) {
    // If clicking the same field, toggle sort order
    if (this.sortBy === sortField) {
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      // New field - default to ascending
      this.sortBy = sortField;
      this.sortOrder = 'asc';
    }
    
    // Reset and fetch with new sort
    this.resetAndFetchGems();
  }

  /**
   * Render table view with all stones as rows
   * 
   * @param {Array} items - The items to render
   * @param {HTMLElement} container - The container element
   */
  renderTableView(items, container) {
    // Create wrapper div for horizontal scroll on mobile
    const tableWrapper = document.createElement('div');
    tableWrapper.className = 'w-full overflow-x-auto';
    
    // Create table element
    const table = document.createElement('table');
    table.className = 'w-full border-collapse min-w-full';
    
    // Create table header
    const thead = document.createElement('thead');
    const headerRow = document.createElement('tr');
    headerRow.className = 'bg-black text-white';
    
    this.tableColumns.forEach(column => {
      const th = document.createElement('th');
      th.className = 'px-4 py-3 text-left text-sm font-medium whitespace-nowrap';
      
      // Use 'Grade' for gemstones, 'Cut' for others
      const label = column.key === 'cut' && this.stoneType === 'gemstone' ? 'Grade' : column.label;
      
      if (column.sortable) {
        // Make header clickable and add sort arrow
        th.className += ' cursor-pointer hover:bg-gray-800 select-none';
        th.setAttribute('role', 'button');
        th.setAttribute('tabindex', '0');
        th.setAttribute('aria-label', `Sort by ${label}`);
        
        // Create container for label and arrow
        const headerContent = document.createElement('div');
        headerContent.className = 'flex items-center justify-between gap-2';
        
        const labelSpan = document.createElement('span');
        labelSpan.textContent = label;
        headerContent.appendChild(labelSpan);
        
        // Add sort arrow icon
        const arrowContainer = document.createElement('span');
        arrowContainer.className = 'flex items-center justify-center';
        arrowContainer.style.width = '24px';
        arrowContainer.style.height = '24px';
        arrowContainer.style.flexShrink = '0';
        
        // Determine if this column is currently sorted
        const isCurrentlySorted = this.sortBy === column.sortField;
        const isAscending = isCurrentlySorted && this.sortOrder === 'asc';
        const isDescending = isCurrentlySorted && this.sortOrder === 'desc';
        
        // Create sort arrow using the provided SVG
        const sortArrow = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        sortArrow.setAttribute('width', '24');
        sortArrow.setAttribute('height', '24');
        sortArrow.setAttribute('viewBox', '0 0 24 24');
        sortArrow.setAttribute('fill', 'none');
        sortArrow.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
        
        // Rotate arrow 180 degrees for ascending sort (pointing up)
        if (isAscending) {
          sortArrow.style.transform = 'rotate(180deg)';
        }
        
        // Set opacity based on sort state
        if (isCurrentlySorted) {
          sortArrow.style.opacity = '1';
        } else {
          sortArrow.style.opacity = '0.3';
        }
        
        const arrowPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        arrowPath.setAttribute('d', 'M7 10L12 15L17 10H7Z');
        arrowPath.setAttribute('fill', 'white');
        sortArrow.appendChild(arrowPath);
        
        arrowContainer.appendChild(sortArrow);
        headerContent.appendChild(arrowContainer);
        
        th.appendChild(headerContent);
        
        // Add click handler for sorting
        th.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.handleColumnSort(column.sortField);
        });
        
        // Add keyboard support
        th.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            this.handleColumnSort(column.sortField);
          }
        });
      } else {
        // Non-sortable column - just add text
        th.textContent = label;
      }
      
      headerRow.appendChild(th);
    });
    
    thead.appendChild(headerRow);
    table.appendChild(thead);
    
    // Create table body
    const tbody = document.createElement('tbody');
    
    items.forEach((item, index) => {
      const row = document.createElement('tr');
      // First row white, second row gray (alternating) - gray is #F8F8F8
      row.className = `${index % 2 === 0 ? 'bg-white' : ''} transition-colors cursor-pointer`;
      
      // Set gray background for odd rows using inline style
      if (index % 2 === 1) {
        row.style.backgroundColor = '#F8F8F8';
      }
      
      // Add hover effect with #e5e5e5 background
      row.addEventListener('mouseenter', () => {
        row.style.backgroundColor = '#e5e5e5';
      });
      
      row.addEventListener('mouseleave', () => {
        // Restore original background color
        if (index % 2 === 1) {
          row.style.backgroundColor = '#F8F8F8';
        } else {
          row.style.backgroundColor = 'white';
        }
      });
      
      // Make entire row clickable
      const slug = window.theme.stones.getStoneSlug(item, this.stoneType);
      let href = `/products/gemstone?stone=${slug}`;
      if (this.stoneType === 'color-diamond' || this.stoneType === 'diamond') {
        href = `/products/diamond?stone=${slug}`;
      } else if (this.stoneType === 'moissanite') {
        href = `/products/moissanite?stone=${slug}`;
      }
      
      row.addEventListener('click', (e) => {
        // Don't navigate if clicking on a link inside the row
        if (e.target.closest('a')) {
          return;
        }
        window.location.href = href;
      });
      
      this.tableColumns.forEach(column => {
        const td = document.createElement('td');
        td.className = 'px-4 py-3 whitespace-nowrap';
        td.innerHTML = column.render(item, this.stoneType);
        row.appendChild(td);
      });
      
      tbody.appendChild(row);
    });
    
    table.appendChild(tbody);
    tableWrapper.appendChild(table);
    container.appendChild(tableWrapper);
  }

  /**
   * Render Load More button and status
   */
  renderLoadMoreButton() {
    const container = this.querySelector('[data-pagination-container]');
    const currentItemsCount = this.currentPage * this.perPage;
    const showingCount = Math.min(currentItemsCount, this.totalItems);
    
    container.innerHTML = `
      <div class="text-center my-9">
        ${this.hasNext ? `<button class="btn btn-primary min-w-[280px] mb-2" data-load-more >Load More</button>` : ``}
        <div class="mt-3 text-sm text-gray-500">
          Showing ${showingCount} of ${this.totalItems} items
          ${this.hasNext ? ` • Page ${this.currentPage} of ${this.totalPages}` : ''}
        </div>
      </div>
    `;

    this.attachLoadMoreEventListener();
  }

  /**
   * Attach event listener to Load More button
   */
  attachLoadMoreEventListener() {
    const loadMoreBtn = this.querySelector('[data-load-more]');
    if (loadMoreBtn) {
      loadMoreBtn.addEventListener('click', () => this.loadMoreItems());
    }
  }

  /**
   * Load next page and append items
   */
  async loadMoreItems() {
    if (!this.hasNext) return;

    this.enableLoading();

    try {
      const nextPage = this.currentPage + 1;
      const result = await this.getStones(nextPage);
      
      // Append new items to existing items
      this.allItems = [...this.allItems, ...result.data];
      
      // Update pagination state
      this.updatePaginationState(result.pagination);
      this.currentPage = nextPage;
      
      // Update URL
      this.updateURL(this.currentPage);
      
      // Re-render items and controls
      this.renderCurrentItems();
      this.renderLoadMoreButton();
      
    } catch (error) {
      console.error('Error loading more items:', error);
    } finally {
      this.disableLoading();
    }
  }

  /**
   * Create a filter
   * 
   * @param {HTMLElement} facet - The facet to create
   * @returns 
   */
  createFilter(name, optionsHtml, message = null) {

    if (!this.filterOptions) return;
    const sidebarFacets = this.querySelector('[data-sidebar-facets]');
    const drawerFacets = this.querySelector('[data-drawer-facets]');
    if (!drawerFacets || !sidebarFacets) return;

    const sidebarFacet = document.createElement('stone-filter-facet');
    sidebarFacet.classList.add('hidden', 'laptop:flex');
    sidebarFacet.setAttribute('type', 'accordion:open');
    sidebarFacet.setAttribute('name', name);
    sidebarFacet.setAttribute('options', optionsHtml);

    if (message) {
      sidebarFacet.setAttribute('message', message);
    }

    const drawerFacet = document.createElement('stone-filter-facet');
    drawerFacet.setAttribute('type', 'accordion');
    drawerFacet.setAttribute('name', name);
    drawerFacet.setAttribute('options', optionsHtml);

    if (message) {
      drawerFacet.setAttribute('message', message);
    }

    drawerFacets.appendChild(drawerFacet);
    sidebarFacets.appendChild(sidebarFacet);
  }

  /**
   * Create shape filter component
   */
  createShapeFilter() {

    // Shape filter
    if (this.filterOptions.shape_ids && this.filterOptions.shape_ids.length > 0) {

      const optionsHtml = `
      <div class="option-tile mb-5" data-columns-count="3">
          <div class="option-tile_options grid grid-cols-4 relative border-l border-t border-base-border [&>label]:border-r [&>label]:border-b [&>label]:border-base-border">
            ${this.filterOptions.shape_ids.map(shapeId => `
              <label for="filter-${shapeId}-${this.stoneType}" data-shape-id="${theme.stones.getShapeName(shapeId)}" class="mb-0 cursor-pointer w-full flex items-center justify-between hover:underline">
                <input
                  type="checkbox"
                  class="mb-0 visually-hidden-checkbox"
                  autocomplete="off"
                  name="shape_ids"
                  value="${shapeId}"
                  id="filter-${shapeId}-${this.stoneType}"
                  ${this.activeFilters.shape_ids && this.activeFilters.shape_ids.includes(shapeId.toString()) ? 'checked' : ''}
                >
                <span class="size-6 bg-cover bg-center bg-no-repeat option-image-center-stone-shape-${theme.stones.getShapeName(shapeId).toLowerCase().replaceAll(' ', '-')}"></span>
                <span class="line-clamp-3 text-xs">${theme.stones.getShapeName(shapeId)}</span>
              </label>
            `).join('')}
        </div>
      </div>`;

      const message = 'Choosing the perfect shape for your engagement ring is about finding the style that reflects your love. Let the shape you choose be a symbol of your bond, crafted to shine beautifully.';

      this.createFilter('Shape', optionsHtml, message);
    }
  }

  /**
   * Create color filter component (for loose diamonds - D to K color grade)
   * 
   * @returns {HTMLElement} The filter element
   */
  createColorFilter() {

    // Best grade first (left) — D is colorless, K has the most tint. GIA grading scale.
    const colors = [
      'D',
      'E',
      'F',
      'G',
      'H',
      'I',
      'J',
      'K'
    ];

    let optionsHtml = `
    <div class="py-4">
      <filter-slider-range
        reference="colors"
        data="${colors.join(',')}"
        step="1"></filter-slider-range>
      <img src="${theme.fileUrl.replace('file-name', 'stone-filter-color.png')}" alt="Diamond color chart" class="w-full block py-6 scale-x-[-1]">
      <div class="flex flex-col gap-3 pb-1">
        <div class="flex gap-3">
          <span class="font-medium text-sm text-center min-w-[48px] shrink-0">D</span>
          <p class="w-full m-0 p-0 text-sm">Completely colorless. This is the total absence of color. Extremely rare, highest value.</p>
        </div>
        <div class="flex gap-3">
          <span class="font-medium text-sm text-center min-w-[48px] shrink-0">E-F</span>
          <p class="w-full m-0 p-0 text-sm">Still colorless. These are still amazing values.</p>
        </div>
        <div class="flex gap-3">
          <span class="font-medium text-sm text-center min-w-[48px] shrink-0">G-H-I</span>
          <p class="w-full m-0 p-0 text-sm">Near colorless. These are still good values. You cannot see color in this range without being trained.</p>
        </div>
        <div class="flex gap-3">
          <span class="font-medium text-sm text-center min-w-[48px] shrink-0">J-K</span>
          <p class="w-full m-0 p-0 text-sm">There is a slight tint.</p>
        </div>
        <p class="w-full m-0 p-0 pt-1 text-xs italic">Based on GIA's grading scale.</p>
      </div>
    </div>`;

    this.createFilter('Color', optionsHtml);
  }

  /**
   * Create Color filter for color diamonds (fancy colors) - tile style like Shape
   * Options: Yellow, Pink, Purple, Red, Blue, Black, Brown, Green, Gray, Orange
   *
   * @returns {void}
   */
  createColorDiamondFilter() {

    const fancyColors = [
      'Yellow',
      'Pink',
      'Purple',
      'Red',
      'Blue',
      'Black',
      'Brown',
      'Green',
      'Gray',
      'Orange'
    ];

    const optionsHtml = `
    <div class="option-tile mb-5" data-columns-count="4">
      <div class="option-tile_options grid grid-cols-4 relative border-l border-t border-base-border [&>label]:border-r [&>label]:border-b [&>label]:border-base-border">
        ${fancyColors.map(color => {
          const isSelected = this.activeFilters.colors && this.activeFilters.colors.includes(color);
          const id = `filter-color-${color.toLowerCase()}-${this.stoneType}`;
          return `
          <label for="${id}" class="mb-0 cursor-pointer w-full flex items-center justify-center hover:underline p-3">
            <input
              type="checkbox"
              class="mb-0 visually-hidden-checkbox"
              autocomplete="off"
              name="colors"
              value="${color}"
              id="${id}"
              ${isSelected ? 'checked' : ''}
            >
            <span class="size-6 bg-cover bg-center bg-no-repeat option-image-color-diamond-color-${color.toLowerCase().replaceAll(' ', '-')}"></span>
            <span class="line-clamp-3 text-xs">${color}</span>
          </label>
          `;
        }).join('')}
      </div>
    </div>`;

    this.createFilter('Color', optionsHtml);
  }

  /**
   * Create gem stone types filter component
   * 
   * @returns {HTMLElement} The filter element
   */
  createGemStoneTypesFilter() {

    let optionsHtml = '';

    // Loop through gem stone types
    this.filterOptions.gem_stone_types.forEach(gemStoneType => {

      // Check if gem stone type is selected
      const isSelected = this.activeFilters.gem_stone_types && this.activeFilters.gem_stone_types.includes(gemStoneType);

      // Create option HTML
      optionsHtml += `
        <label for="filter-${gemStoneType.toLowerCase().replace(/\s+/g, '-')}-${this.stoneType}" class="mb-0 cursor-pointer w-full flex items-center justify-center hover:underline p-3">
          <input
            type="checkbox"
            class="mb-0 visually-hidden-checkbox"
            autocomplete="off"
            name="gem_stone_types"
            value="${gemStoneType}"
            id="filter-${gemStoneType.toLowerCase().replace(/\s+/g, '-')}-${this.stoneType}"
            ${isSelected ? 'checked' : ''}
          >
          <span class="size-6 bg-cover bg-center bg-no-repeat option-image-gemstone-${theme.handleize(gemStoneType)}"></span>
          <span class="line-clamp-3 text-xs">${gemStoneType}</span>
        </label>
      `;
    });

    optionsHtml = `
    <div class="option-tile mb-5" data-columns-count="4">
      <div class="option-tile_options grid grid-cols-4 relative border-l border-t border-base-border [&>label]:border-r [&>label]:border-b [&>label]:border-base-border">
        ${optionsHtml}
      </div>
    </div>
    `;

    const filterMessage = 'Consider personal touches like birthstones or gems with sentimental value. Focus on color, quality, and durability, ensuring it reflects both style and meaning. A gemstone can add a unique, personal connection to your ring.';

    this.createFilter('Gemstone', optionsHtml, filterMessage);
  }

  /**
   * Create carat range filter component
   * 
   * @returns {HTMLElement} The filter element
   */
  createCaratRangeFilter() {

    const { min, max } = this.filterOptions.carat_range;

    let optionsHtml = `
    <div class="min-h-[100px] border-b border-base-border w-full">
      <filter-slider-range
        reference="carat_range"
        min="${min}"
        max="${max}"
        step="1"
        suffix="ct"></filter-slider-range>
    </div>
    `;

    const message = 'The term “carat weight” is often confused with size, however, in actuality, a carat is a measure of a diamond\'s weight. To choose the best carat weight for your diamond ring, consider the size of your finger, the size of the jewelry setting, and your budget.';

    this.createFilter('Carat', optionsHtml, message);
  }

  /**
   * Create price range filter component
   * 
   * @returns {HTMLElement} The filter element
   */
  createPriceRangeFilter() {

    const { min, max } = this.filterOptions.price_range;

    let optionsHtml = `
    <div class="min-h-[100px] border-b border-base-border w-full">
      <filter-slider-range
        reference="price"
        min="${min}"
        max="${max}"
        step="1"
        prefix="$"></filter-slider-range>
    </div>
    `;

    this.createFilter('Price', optionsHtml);
  }

  /**
   * Create origin filter component
   * 
   * @returns {HTMLElement} The filter element
   */
  createOriginFilter() {

    let optionsHtml = '';

    let isLabGrown = false;
    let hasLabGrownFilter = false;

    if (this.activeFilters.lab_grown === 'true') {
      isLabGrown = true;
      hasLabGrownFilter = true;
    } else if (this.activeFilters.lab_grown === 'false') {
      hasLabGrownFilter = true;
    }

    // Handle legacy array format for backward compatibility
    if (this.activeFilters.lab_grown?.length > 0) {
      hasLabGrownFilter = true;
      if (this.activeFilters.lab_grown[0] === 'true') {
        isLabGrown = true;
      }
    }
    
    // All Origins
    optionsHtml += `
      <label for="filter-all-origins-${this.stoneType}" class="mb-0 cursor-pointer w-full flex items-center justify-between hover:underline">
        <input type="radio" class="mb-0 visually-hidden-checkbox" autocomplete="off" name="lab_grown" value="" ${!hasLabGrownFilter ? 'checked' : ''} id="filter-all-origins-${this.stoneType}">
        <span>All</span>
      </label>
    `;
    
    // Natural
    optionsHtml += `
      <label for="filter-natural-${this.stoneType}" class="mb-0 cursor-pointer w-full flex items-center justify-between hover:underline">
        <input type="radio" class="mb-0 visually-hidden-checkbox" autocomplete="off" name="lab_grown" value="false" ${hasLabGrownFilter && !isLabGrown ? 'checked' : ''} id="filter-natural-${this.stoneType}">
        <span>Natural</span>
      </label>
    `;

    // Lab-grown
    optionsHtml += `

      <label for="filter-lab-grown-${this.stoneType}" class="mb-0 cursor-pointer w-full flex items-center justify-between hover:underline">
        <input type="radio" class="mb-0 visually-hidden-checkbox" autocomplete="off" name="lab_grown" value="true" ${isLabGrown ? 'checked' : ''} id="filter-lab-grown-${this.stoneType}">
        <span>Lab Created</span>
      </label>
    `;

    optionsHtml = `
    <div class="option-tile mb-5" data-columns-count="3">
      <div class="option-tile_options grid grid-cols-3 relative border-l border-t border-base-border [&>label]:border-r [&>label]:border-b [&>label]:border-base-border">
        ${optionsHtml}
      </div>
    </div>
    `;

    this.createFilter('Origin', optionsHtml);
  }

  /**
   * Create stone filter component
   * 
   * @returns {HTMLElement} The filter element
   */
  createStoneFilter() {
  
    let optionsHtml = `
    <div class="option-tile mb-5" data-columns-count="2">
      <div class="option-tile_options grid grid-cols-2 relative border-l border-t border-base-border [&>a]:border-r [&>a]:border-b [&>a]:border-base-border">
      
        <a href="/collections/loose-diamonds" class="${this.stoneType == 'diamond' ? 'active' : ''} mb-0 text-sm cursor-pointer w-full flex items-center justify-between hover:underline">
          Diamond
        </a>

        <a href="/collections/color-diamonds" class="${this.stoneType == 'color-diamond' ? 'active' : ''} mb-0 text-sm cursor-pointer w-full flex items-center justify-between hover:underline">
          Color Diamond
        </a>

        <a href="/collections/gemstones" class="${this.stoneType == 'gemstone' ? 'active' : ''} mb-0 text-sm cursor-pointer w-full flex items-center justify-between hover:underline">
          Gemstone
        </a>

        <a href="/collections/moissanites" class="${this.stoneType == 'moissanite' ? 'active' : ''} mb-0 text-sm cursor-pointer w-full flex items-center justify-between hover:underline">
          Moissanite
        </a>

      </div>
    </div>
    `;

    this.createFilter('Stone', optionsHtml);
  }

  /**
   * Create cut filter component
   * 
   * @returns {HTMLElement} The filter element
   */
  createCutFilter() {

    // Best grade first (left) — GIA cut scale runs Excellent down to Poor.
    // Legacy data also contains a small number of "Ideal" stones, which GIA does not
    // grade; StoneFilters.expandCutFilter() folds those into the "Excellent" stop so
    // they stay reachable.
    const options = [
      'Excellent',
      'Very Good',
      'Good',
      'Fair',
      'Poor'
    ];

    let optionsHtml = `
    <div class="py-4">
      <filter-slider-range
        reference="cut"
        class="text-xs"
        data="${options.join(',')}"
        step="1"></filter-slider-range>
      <div class="flex flex-col gap-3 pt-5 pb-3">
        <p class="w-full m-0 p-0 text-sm">Cut is how well a diamond's proportions, symmetry, and polish interact with light. This will affect its brilliance, fire and sparkle. It is often considered the most important of the 4Cs.</p>
        <div class="flex gap-3">
          <span class="font-medium text-sm text-right w-8 shrink-0 min-w-[90px]">Excellent</span>
          <p class="w-full m-0 p-0 text-sm">Maximum brilliance and fire. Ideal proportions and light performance. Highest value and most desirable.</p>
        </div>
        <div class="flex gap-3">
          <span class="font-medium text-sm text-right w-8 shrink-0 min-w-[90px]">Very Good</span>
          <p class="w-full m-0 p-0 text-sm">Very strong sparkle, minor proportion differences from excellent. A great value alternative.</p>
        </div>
        <div class="flex gap-3">
          <span class="font-medium text-sm text-right w-8 shrink-0 min-w-[90px]">Good</span>
          <p class="w-full m-0 p-0 text-sm">Can have a noticeable reduction in sparkle compared to higher grades. Still acceptable appearance.</p>
        </div>
        <div class="flex gap-3">
          <span class="font-medium text-sm text-right w-8 shrink-0 min-w-[90px]">Fair</span>
          <p class="w-full m-0 p-0 text-sm">Reduced brilliance and life. Light leakage might be present.</p>
        </div>
        <div class="flex gap-3">
          <span class="font-medium text-sm text-right w-8 shrink-0 min-w-[90px]">Poor</span>
          <p class="w-full m-0 p-0 text-sm">Significant light loss. Dull or glassy appearance.</p>
        </div>
        <p class="w-full m-0 p-0 pt-1 text-xs italic">Based on GIA's grading scale.</p>
      </div>
    </div>`;

    this.createFilter('Cut', optionsHtml);
  }

  /**
   * Create clarity filter component
   * 
   * @returns {HTMLElement} The filter element
   */
  createClarityFilter() {

    // Best grade first (left) — GIA clarity scale runs FL down to I1.
    // "IF" is a real value in the catalog and was previously missing from this slider.
    const options = [
      'FL',
      'IF',
      'VVS1',
      'VVS2',
      'VS1',
      'VS2',
      'SI1',
      'SI2',
      'SI3',
      'I1'
    ];

    let optionsHtml = `
    <div class="py-4">
      <filter-slider-range
        reference="clarity"
        class="text-xs"
        data="${options.join(',')}"
        step="1"></filter-slider-range>
      <div class="flex flex-col gap-3 pt-5 pb-3">
        <div class="flex gap-3">
          <span class="font-medium text-sm text-right min-w-[90px] w-8 shrink-0">FL</span>
          <p class="w-full m-0 p-0 text-sm">Flawless &mdash; There are no inclusions or blemishes under 10x magnification. This is extremely rare and valuable.</p>
        </div>
        <div class="flex gap-3">
          <span class="font-medium text-sm text-right min-w-[90px] w-8 shrink-0">IF</span>
          <p class="w-full m-0 p-0 text-sm">Internally Flawless &mdash; Internally the stone is flawless but there are minor surface blemishes possible.</p>
        </div>
        <div class="flex gap-3">
          <span class="font-medium text-sm text-right min-w-[90px] w-8 shrink-0">VVS1 & VVS2</span>
          <p class="w-full m-0 p-0 text-sm">Very Very Slightly Included &mdash; Minute inclusions are extremely difficult to see at 10x magnification.</p>
        </div>
        <div class="flex gap-3">
          <span class="font-medium text-sm text-right min-w-[90px] w-8 shrink-0">VS1 & VS2</span>
          <p class="w-full m-0 p-0 text-sm">Very Slightly Included &mdash; Minor inclusions are difficult to see at 10x magnification.</p>
        </div>
        <div class="flex gap-3">
          <span class="font-medium text-sm text-right min-w-[90px] w-8 shrink-0">SI1 - SI3</span>
          <p class="w-full m-0 p-0 text-sm">Slightly Included &mdash; There are noticeable inclusions at 10x magnification. Usually these inclusions are not able to see with the naked eye.</p>
        </div>
        <div class="flex gap-3">
          <span class="font-medium text-sm text-right min-w-[90px] w-8 shrink-0">I1</span>
          <p class="w-full m-0 p-0 text-sm">Included &mdash; These diamonds might have inclusions that are visible to the naked eye.</p>
        </div>
        <p class="w-full m-0 p-0 pt-1 text-xs italic">Based on GIA's grading scale.</p>
      </div>
    </div>`;

    this.createFilter('Clarity', optionsHtml);
  }
}

/**
 * Stone Filter Facet
 */
class StoneFilterFacet extends HTMLElement {

  constructor() {
    super();
  }

  connectedCallback() {
    this.render();
  }

  static get observedAttributes() {
    return ['type', 'name', 'options', 'message'];
  }

  get type() {
    return this.getAttribute('type');
  }

  set type(value) {
    this.setAttribute('type', value);
  }

  get name() {
    return this.getAttribute('name');
  }

  set name(value) {
    this.setAttribute('name', value);
  }

  get options() {
    return this.getAttribute('options');
  }

  set options(value) {
    this.setAttribute('options', value);
  }

  get message() {
    return this.getAttribute('message');
  }

  set message(value) {
    this.setAttribute('message', value);
  }

  render() {

    if (this.type === 'dropdown') {

      this.innerHTML = `
        <details class="block relative group">
          <summary class="btn btn-ghost rounded-none active:border-primary-500 group-open:border-primary-500 group-open:before:content-[''] group-open:before:fixed group-open:before:bg-transparent group-open:before:w-full group-open:before:h-full  group-open:before:left-0 group-open:before:right-0 group-open:before:top-0 group-open:before:bottom-0 group-open:before:z-10">
            <span>${this.name}</span>
            <span class="size-5 transition-transform duration-300 group-open:rotate-180">
              <svg width="100%" height="100%" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M5.29289 7.29289C5.68342 6.90237 6.31658 6.90237 6.70711 7.29289L10 10.5858L13.2929 7.29289C13.6834 6.90237 14.3166 6.90237 14.7071 7.29289C15.0976 7.68342 15.0976 8.31658 14.7071 8.70711L10.7071 12.7071C10.3166 13.0976 9.68342 13.0976 9.29289 12.7071L5.29289 8.70711C4.90237 8.31658 4.90237 7.68342 5.29289 7.29289Z" fill="currentColor"/>
              </svg>
            </span>
          </summary>
          <div class="absolute top-[calc(100%+10px)] left-0 bg-white border border-primary-500 z-20 min-w-[250px]" style="width: 480px;">
            <p class="px-4 py-5 m-0 text-sm">${this.message}</p>
            ${this.options}
          </div>
        </details>
      `;

    } else if (this.type === 'accordion' || this.type === 'accordion:open') {

      this.innerHTML = `
        <component-accordion ${this.type === 'accordion:open' ? 'open' : ''} class="group w-full border-b border-base-border">
          <div class="uppercase text-sm pt-[13px] pb-[12px] flex items-center gap-2 justify-between" data-accordion-trigger>
            ${this.name}
            <span class="w-5 h-5 transition-transform duration-300 group-[.active]:rotate-180">
              <svg width="100%" height="100%" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M5.29289 7.29289C5.68342 6.90237 6.31658 6.90237 6.70711 7.29289L10 10.5858L13.2929 7.29289C13.6834 6.90237 14.3166 6.90237 14.7071 7.29289C15.0976 7.68342 15.0976 8.31658 14.7071 8.70711L10.7071 12.7071C10.3166 13.0976 9.68342 13.0976 9.29289 12.7071L5.29289 8.70711C4.90237 8.31658 4.90237 7.68342 5.29289 7.29289Z" fill="currentColor"/>
              </svg>
            </span>
          </div>
          <div data-accordion-container class="p-[1px]">
            ${ this.message ? `<p class="pb-5 m-0 text-sm">${this.message}</p>` : '' }
            ${this.options}
          </div>
        </component-accordion>
      `;

    }
  }
}

/**
 * Stone Filter Remove
 */
class StoneFilterRemove extends HTMLElement {

  constructor() {
    super();

    // Setup accessibility and event listeners
    this.setAttribute('role', 'button');
    this.setAttribute('aria-label', 'Remove filter');
    this.setAttribute('tabindex', '0');
  }
  
  connectedCallback() {
    this.render();
    this.initEvents();
  }

  get key() {
    return this.getAttribute('key');
  }

  set key(value) {
    this.setAttribute('key', value);
  }

  get value() {
    return this.getAttribute('value');
  }

  set value(value) {
    this.setAttribute('value', value);
  }

  get label() {
    return this.getAttribute('label');
  }

  set label(value) {
    this.setAttribute('label', value);
  }

  initEvents() {

    // Bind the handleClick method to the current instance
    this.handleClick = this.handleClick.bind(this);

    // Add event listeners for click and keyup events
    this.addEventListener('click', this.handleClick);

    // Add event listener for space/enter key to trigger click event
    this.addEventListener('keyup', (event) => {

      if (event.code.toUpperCase() === 'SPACE' || event.code.toUpperCase() === 'ENTER') {
        event.preventDefault();
        this.handleClick(event);
      }
    });
  }
  
  /**
   * Handle filter removal
   * @param {Event} event - The event that triggered the filter removal
   */
  handleClick(event) {

    event.preventDefault();

    const form = this.closest('stone-filters') || document.querySelector('stone-filters');

    if (this.dataset.reference === 'reset-all') {

      form?.resetAllFilters();

    } else {

      form?.removeActiveFilter(this.key, this.value);
    }
  }

  /**
   * Render the filter remove button
   */
  render() {

    this.innerHTML = `<button class="bg-disabled-background px-2 py-[5px] flex justify-between items-center border-primary-300 gap-[5px] hover:no-underline hover:bg-[#F3F2F2]" data-filter-remove>
      <div>
        <div class="text-[12px] leading-[12px] uppercase">${this.label}</div>
      </div>
      <div class="no-underline size-4" aria-label="Remove">
        <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M18.1872 5.81292C18.4801 6.10581 18.4801 6.58068 18.1872 6.87358L13.0607 12.0001L18.1872 17.1266C18.4801 17.4195 18.4801 17.8944 18.1872 18.1873C17.8943 18.4802 17.4194 18.4802 17.1265 18.1873L12 13.0608L6.87348 18.1873C6.58058 18.4802 6.10571 18.4802 5.81282 18.1873C5.51992 17.8944 5.51992 17.4195 5.81282 17.1266L10.9393 12.0001L5.81282 6.87358C5.51992 6.58068 5.51992 6.10581 5.81282 5.81292C6.10571 5.52002 6.58058 5.52002 6.87348 5.81292L12 10.9394L17.1265 5.81292C17.4194 5.52002 17.8943 5.52002 18.1872 5.81292Z" fill="currentColor"/>
        </svg>
      </div>
    </button>`;
  }
}

class FilterSliderRange extends HTMLElement {

  static get observedAttributes() {
    return ['reference', 'min', 'max', 'step', 'data'];
  }

  constructor() {
    super();
  }

  connectedCallback() {
    this.render();
    this.initEvents();
    this.keepEdgeLabelsInView();
  }

  /**
   * tc-range-slider centres each mark label on its mark, so the first and last labels
   * overhang the track by half their width. At full width that overhang falls outside
   * the facet and gets clipped — "Excellent" on the Cut filter rendered as "xcellent".
   *
   * Inset the track by half the widest end label so the overhang lands inside the facet.
   * The labels keep their natural centred spacing (pinning them to the track ends
   * instead makes "Excellent" collide with "Very Good").
   *
   * The track width comes from the --width custom property the slider already reads, set
   * on the host so it beats the component's own :host default. It must be an absolute
   * length: the host is a flex container, so a percentage would resolve against an
   * indefinite containing block and collapse the track to zero.
   *
   * @param {number} attempt - Current retry, while the slider upgrades itself
   * @returns {void}
   */
  keepEdgeLabelsInView(attempt = 0) {

    const slider = this.querySelector('tc-range-slider');

    if (!slider) {
      return;
    }

    // The slider attaches its shadow root when the custom element upgrades, which may
    // not have happened yet on first paint.
    if (!slider.shadowRoot) {

      if (attempt < 20) {
        requestAnimationFrame(() => this.keepEdgeLabelsInView(attempt + 1));
      }

      return;
    }

    const applyInset = () => {

      const labels = slider.shadowRoot.querySelectorAll('.mark-value');

      if (!labels.length) {
        return;
      }

      // Facets start hidden on mobile and inside the filter drawer, where everything
      // measures zero. Leave the slider alone until it is actually laid out — the
      // observer below re-runs this as soon as it becomes visible.
      const hostWidth = slider.getBoundingClientRect().width;

      if (!hostWidth) {
        return;
      }

      const firstWidth = labels[0].getBoundingClientRect().width;
      const lastWidth = labels[labels.length - 1].getBoundingClientRect().width;
      const inset = Math.ceil(Math.max(firstWidth, lastWidth) / 2);

      if (!inset) {
        return;
      }

      slider.style.setProperty('--width', `${Math.max(0, hostWidth - inset * 2)}px`);
    };

    applyInset();

    // Recompute when the facet is revealed (mobile drawer, laptop breakpoint) or the
    // viewport changes. Observing the host is safe: its width is set by the layout, not
    // by --width, so this cannot feed back into itself.
    if (typeof ResizeObserver !== 'undefined') {
      new ResizeObserver(applyInset).observe(slider);
    }
  }

  get reference() {
    return this.getAttribute('reference');
  }

  get min() {
    return this.getAttribute('min');
  }

  get max() {
    return this.getAttribute('max');
  }

  get step() {
    return this.getAttribute('step');
  }

  get data() {
    return this.getAttribute('data');
  }

  set data(value) {
    this.setAttribute('data', value);
  }

  get prefix() {
    return this.getAttribute('prefix');
  }

  get suffix() {
    return this.getAttribute('suffix');
  }

  /**
   * Initialize events
   */
  initEvents() {

    this.debouncedOnFilterChange = window.theme.debounce.apply(event => {
      this.onFilterChange(event);
    }, 500);

    this.debouncedOnFilterInputViewChange = window.theme.debounce.apply(event => {
      this.onFilterInputViewChange(event);
    }, 500);

    // Handle filter change
    this.querySelector('tc-range-slider').addEventListener('change', this.debouncedOnFilterChange.bind(this));

    // Handle filter view change
    this.querySelector('tc-range-slider').addEventListener('change', this.onFilterViewChange.bind(this));

    // Handle filter input view change
    let viewValue1 = this.querySelector('input[name="' + this.reference + '_value_1"]');
    let viewValue2 = this.querySelector('input[name="' + this.reference + '_value_2"]');

    if (viewValue1) {
      viewValue1.addEventListener('change', this.debouncedOnFilterInputViewChange.bind(this));
    }

    if (viewValue2) {
      viewValue2.addEventListener('change', this.debouncedOnFilterInputViewChange.bind(this));
    }
  }

  /**
   * Handle filter input view change
   * @param {Event} event - The event object
   */
  onFilterInputViewChange(event) {

    event.preventDefault();

    let values = [];

    this.querySelectorAll('[data-view-value]').forEach(input => {
      values.push(input.value);
    });

    this.setValues(values);
  }

  /**
   * Handle filter view change
   * @param {Event} event - The event object
   */
  onFilterViewChange(event) {

    let viewValue1 = this.querySelector('input[name="' + this.reference + '_value_1"]');
    let viewValue2 = this.querySelector('input[name="' + this.reference + '_value_2"]');

    if (viewValue1) {
      viewValue1.value = event.detail.value1;
    }

    if (viewValue2) {
      viewValue2.value = event.detail.value2;
    }
  }

  /**
   * Handle filter change
   * @param {Event} event - The event object
   */
  onFilterChange(event) {

    let values = [];

    if (this.data && this.data.length > 0) {

      let data = this.data.split(',');
      let value1Index = data.indexOf(event.detail.value1);
      let value2Index = data.indexOf(event.detail.value2);
      
      // Get values between value1 and value2
      values = data.slice(value1Index, value2Index + 1);

    } else {

      values.push(event.detail.value1);
      values.push(event.detail.value2);
    }

    this.setValues(values);
  }

  /**
   * Set values in the input
   * 
   * @param {Array} values - The values to set
   */
  setValues(values) {
    let input = this.querySelector('input[name="' + this.reference + '"]');
    input.value = values.join(',');
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Get values from the input
   * 
   * @returns {Array} - The values from the input
   */
  getValuesFromURL() {
    let url = new URL(window.location.href);
    
    // Handle range filters that use min/max parameters
    if (this.reference === 'carat_range') {
      const min = url.searchParams.get('carat_min');
      const max = url.searchParams.get('carat_max');
      if (min && max) {
        return [min, max];
      } else if (min || max) {
        return [min || max, min || max];
      }
      // Fall back to checking for carat_range parameter (backward compatibility)
      const values = url.searchParams.get('carat_range');
      return values ? values.split(',') : null;
    } else if (this.reference === 'price') {
      const min = url.searchParams.get('price_min');
      const max = url.searchParams.get('price_max');
      if (min && max) {
        return [min, max];
      } else if (min || max) {
        return [min || max, min || max];
      }
      // Fall back to checking for price parameter (backward compatibility)
      const values = url.searchParams.get('price');
      return values ? values.split(',') : null;
    }

    // Every other slider-backed filter (cut, clarity, colors) is a list of grades.
    return readListParam(url.searchParams, this.reference);
  }

  /**
   * Render component
   */
  render() {

    let values = this.getValuesFromURL();
    let firstValue = null;
    let lastValue = null;
    let min = null;
    let max = null;
    let html = '';

    if (this.data && this.data.length > 0) {

      let count = this.data.split(',').length;
      let data = this.data.split(',');

      min = data[0];
      max = data[data.length - 1];
      firstValue = values ? values[0] : min;
      lastValue = values ? values[values.length - 1] : max;

      html = `
        <tc-range-slider
        class="w-full h-12 flex justify-center"
        data-filter-range="${this.reference}"
        slider-bg="#DBDBDB"
        slider-bg-fill="#825C63"
        pointer-bg="#825C63"
        pointer-bg-hover="#825C63"
        pointer-bg-focus="#825C63"
        pointer-border="none"
        pointer-border-hover="none"
        pointer-border-focus="none"
        pointer-border-active="none"
        pointer-shadow="none"
        pointer-shadow-hover="none"
        pointer-shadow-focus="none"
        data="${this.data}"
        marks="true"
        marks-count="${count}"
        marks-values-count="${count}"
        step="1"
        min="${min}"
        max="${max}"
        value1="${firstValue}"
        value2="${lastValue}"
        generate-labels="true"
        round="0">
        </tc-range-slider>
      `;

    } else {

      min = parseFloat(this.min);
      max = parseFloat(this.max);

      if (values) {

        firstValue = parseInt(values[0]);
        lastValue = parseInt(values[values.length - 1]);

      } else {

        firstValue = min;
        lastValue = max;
      }

      html = `
        <tc-range-slider
        class="w-full h-7 mt-2 flex justify-center"
        data-filter-range="${this.reference}"
        slider-bg="#DBDBDB"
        slider-bg-fill="#825C63"
        pointer-bg="#825C63"
        pointer-bg-hover="#825C63"
        pointer-bg-focus="#825C63"
        pointer-border="none"
        pointer-border-hover="none"
        pointer-border-focus="none"
        pointer-border-active="none"
        pointer-shadow="none"
        pointer-shadow-hover="none"
        pointer-shadow-focus="none"
        step="0.01"
        min="${min}"
        max="${max}"
        value1="${firstValue}"
        value2="${lastValue}"
        generate-labels="true">
        </tc-range-slider>
        <div class="flex gap-10 items-center justify-between">
          <div class="relative">
            ${this.prefix ? `<span class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-600 pointer-events-none">${this.prefix}</span>` : ''}
            <input type="number" name="${this.reference}_value_1" data-view-value="true" value="${firstValue || ''}" class="${this.prefix ? 'pl-6' : ''} ${this.suffix ? 'pr-6' : ''}">
            ${this.suffix ? `<span class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600 pointer-events-none">${this.suffix}</span>` : ''}
          </div>
          <div class="relative">
            ${this.prefix ? `<span class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-600 pointer-events-none">${this.prefix}</span>` : ''}
            <input type="number" name="${this.reference}_value_2" data-view-value="true" value="${lastValue || ''}" class="${this.prefix ? 'pl-6' : ''} ${this.suffix ? 'pr-6' : ''}">
            ${this.suffix ? `<span class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600 pointer-events-none">${this.suffix}</span>` : ''}
          </div>
        </div>
      `;
    }

    this.innerHTML = `${html}<input type="text" style="display: none;" name="${this.reference}" value="${values?.join(',') || ''}">`;
  }
}

if (!customElements.get('filter-slider-range')) {
  customElements.define('filter-slider-range', FilterSliderRange);
}

if (!customElements.get('stone-filter-remove')) {
  customElements.define('stone-filter-remove', StoneFilterRemove);
}

if (!customElements.get('stone-filter-facet')) {
  customElements.define('stone-filter-facet', StoneFilterFacet);
}

if (!customElements.get('stone-filters')) {
  customElements.define('stone-filters', StoneFilters);
}
